import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { navigateToUrl } from 'single-spa';

import Avatar from '@ovpn/ui/avatar';
import Divider from '@ovpn/ui/divider';
import Icon from '@ovpn/ui/icon';
import Listbox from '@ovpn/ui/listbox';
import { useSidebarControl } from '@ovpn/ui/sidebar';
import AccountMenu from '@ovpn/ui/sidebar/account-menu';
import Text from '@ovpn/ui/text';

import {
    BillingEvents,
    redirectToSSO,
    routes,
    selectFrieldlyRole,
    selectIsLdapUser,
    selectIsOwner,
    selectIsSamlUser,
    selectUserName,
} from '@cloud/shared';

import { useAppDispatch } from '../../store';

export function SidebarFooter() {
    const dispatch = useAppDispatch();
    const closeSidebar = useSidebarControl();

    const isSaml = useSelector(selectIsSamlUser);
    const isLdap = useSelector(selectIsLdapUser);
    const username = useSelector(selectUserName);
    const isOwner = useSelector(selectIsOwner);
    const friendlyRole = useSelector(selectFrieldlyRole);
    const isIdpUser = (isSaml || isLdap) && !isOwner;

    const handleChange = useCallback(
        (id: string) => {
            closeSidebar();

            switch (id) {
                case 'account-menu-profile':
                    navigateToUrl(routes.ssoAccount);
                    break;

                case 'account-menu-logout':
                    navigateToUrl(redirectToSSO(window.location.origin, 'logout'));
                    break;

                case 'account-menu-subscription':
                    dispatch(
                        BillingEvents.openBillingModal({
                            actionSource: 'EDIT_SUBSCRIPTION',
                            fallbackRedirectUrl:
                                '/billing/subscriptions/current/edit?action=upgrade',
                        }),
                    );
                    break;

                case 'account-menu-my-subscriptions':
                    dispatch(
                        BillingEvents.openBillingModal({
                            actionSource: 'MY_SUBSCRIPTIONS',
                            fallbackRedirectUrl: '/billing/subscriptions',
                        }),
                    );
                    break;
            }
        },
        [dispatch, closeSidebar],
    );

    return (
        <AccountMenu
            name={username}
            role={friendlyRole}
            avatar={
                <Avatar size="lg" appearance="dark">
                    <Text as="figcaption" aria-label={username}>
                        {username && String(username[0]).toUpperCase()}
                    </Text>
                </Avatar>
            }
        >
            <Listbox id="account-menu-items" role="menu" onChange={handleChange}>
                {!isIdpUser &&
                    ((
                        <Listbox.Option id="account-menu-profile">My Account</Listbox.Option>
                    ) as any)}
                {isOwner &&
                    ((
                        <Listbox.Option id="account-menu-subscription">
                            Edit Subscription
                        </Listbox.Option>
                    ) as any)}
                {isOwner &&
                    ((
                        <Listbox.Option id="account-menu-my-subscriptions">
                            My Subscriptions
                        </Listbox.Option>
                    ) as any)}
                <Divider />
                <Listbox.Option id="account-menu-logout" suffix={<Icon name="logout" />}>
                    Logout
                </Listbox.Option>
            </Listbox>
        </AccountMenu>
    );
}
